import React, { useRef, useState } from "react";
import { useContext } from "react";
import styled from "styled-components";
import { MiniStoreThemeContext } from "../../../MiniStoreContexts/MiniStoreThemeContext";
import { Link, useLocation } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import { CiLogout } from "react-icons/ci";
import { GoTriangleDown } from "react-icons/go";
import { GoTriangleUp } from "react-icons/go";
import { useSession } from "../../../authentications/SessionContext";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CaretDownIcon, CaretUpIcon } from "@radix-ui/react-icons";

const MsNavButtons = () => {
  const { theme } = useContext(MiniStoreThemeContext);
  const location = useLocation();
  const { sessionInfo, authenticated, handleLogout } = useSession();
  const { name } = sessionInfo;
  const [isOpen, setIsOpen] = useState(false);
  const spanRef = useRef(null);
  // console.log("sessionInfo", sessionInfo);
  const getMainDomainUrl = () => {
    const { hostname } = window.location;
    if (
      hostname === "127.0.0.1" ||
      hostname === "localhost" ||
      /^[\d.]+$/.test(hostname) // matches IPs like 192.168.1.10
    ) {
      return `${window.location.protocol}//${hostname}:3000/merchant/signup`;
    }
    const domainParts = hostname.split(".");
    const len = domainParts.length;
    // Fallback if domain is too short
    if (len < 2) return `${window.location.origin}/merchant/signup`;
    const mainDomain = domainParts.slice(-2).join(".");
    return `https://www.${mainDomain}/merchant/signup`;
  };

  return (
    <MsNavButtonsContainer style={{ position: "relative" }}>
      {!authenticated && (
        <StyledButton
          to="/signin"
          state={{ from: location.pathname }}
          theme={theme}
          variant="secondary"
        >
          Sign In
        </StyledButton>
      )}
      {authenticated && (
        <DropdownMenu.DropdownMenu onOpenChange={setIsOpen}>
          <DropdownMenu.Trigger asChild>
            <StyledSpan ref={spanRef} className="cursor-pointer" role="button">
              <SpanText>{name || "User"}</SpanText>
              {isOpen ? (
                <StyledGoTriangleUp size={5} />
              ) : (
                <StyledGoTriangleDown size={5} />
              )}
            </StyledSpan>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <StyledDropdownContent
              className="bg-white  p-2 rounded-md"
              align="start"
              side="bottom"
              sideOffset={0}
              theme={theme}
            >
              <StyledDropdownItem
                onClick={handleLogout}
                className="cursor-pointer text-center p-2"
                theme={theme}
              >
                <LogoutIcon theme={theme} />
                Logout
              </StyledDropdownItem>
            </StyledDropdownContent>
          </DropdownMenu.Portal>
        </DropdownMenu.DropdownMenu>
      )}
      <StyledAnchor
        href={getMainDomainUrl()}
        target="_self"
        rel="noopener noreferrer"
        theme={theme}
        variant="primary"
      >
        Sign Up <FaChevronRight />
      </StyledAnchor>
    </MsNavButtonsContainer>
  );
};

export default MsNavButtons;

const MsNavButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  @media (max-width: 485px) {
    gap: 0.25rem;
  }
`;
const StyledAnchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  font-family: "Outfit", sans-serif;
  border-radius: 0.15rem;
  width: auto;
  height: 45px;
  padding: 10px 20px;
  transition: all 0.3s ease-in-out;
  border: none;
  cursor: pointer;

  ${({ theme, variant }) =>
    variant === "primary"
      ? `
    background-color: ${theme.primaryColor.primary};
    color: ${theme.textColor.primary};
    
    &:hover {
      background-color: ${theme.primaryColor.primary};
      color: ${theme.textColor.primary};
      opacity: 1;
    }
  `
      : `
    background-color: ${theme.primaryColor.secondary};
    color: ${theme.textColor.secondary};
    
    &:hover {
      background-color: ${theme.primaryColor.secondary};
      color: ${theme.textColor.secondary};
      opacity: 1;
    }
    `}
  @media (max-width: 560px) {
    font-size: 16px;
  }
  @media (max-width: 485px) {
    font-size: 12px;
    height: 45px;
    padding: 5px 10px;
  }
`;

const StyledButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  font-family: "Outfit", sans-serif;
  border-radius: 0.15rem;
  width: auto;
  height: 45px;
  padding: 10px 20px;
  transition: all 0.3s ease-in-out;
  border: none;
  cursor: pointer;

  ${({ theme, variant }) =>
    variant === "primary"
      ? `
    background-color: ${theme.primaryColor.primary};
    color: ${theme.textColor.primary};
    
    &:hover {
      background-color: ${theme.primaryColor.primary}; /* Keeps the same */
      color: ${theme.textColor.primary}; /* Keeps the same */
      opacity: 1; /* Ensures no transparency change */
    }
  `
      : `
    background-color: ${theme.primaryColor.secondary};
    color: ${theme.textColor.secondary};
    
    &:hover {
      background-color: ${theme.primaryColor.secondary}; /* Keeps the same */
      color: ${theme.textColor.secondary}; /* Keeps the same */
      opacity: 1; /* Ensures no transparency change */
    }
    `}
  @media (max-width: 560px) {
    font-size: 16px;
  }
  @media (max-width: 485px) {
    font-size: 12px;
    height: 45px;
    padding: 5px 10px;
  }
`;
const LogoutIcon = styled(CiLogout)`
  color: ${({ theme }) => theme.primaryColor.primary};
  stroke-width: 2.5;
  margin-right: 5px;
  @media (max-width: 485px) {
    margin-right: 3px;
  }
`;
const StyledGoTriangleUp = styled(GoTriangleUp)`
  margin-left: 5px;
  height: 24px;
  width: 24px;
  stroke-width: 0.5;
  vertical-align: baseline;
  @media (max-width: 485px) {
    margin-left: 0px;
    height: 18px;
    width: 18px;
    vertical-align: super;
  }
`;
const StyledGoTriangleDown = styled(GoTriangleDown)`
  margin-left: 5px;
  height: 24px;
  width: 24px;
  stroke-width: 0.5;
  @media (max-width: 485px) {
    margin-left: 0px;
    height: 18px;
    width: 18px;
    vertical-align: super;
  }
`;
const StyledSpan = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  padding: 0.5rem;
  font-family: Outfit, sans-serif;
  text-decoration: none;
  color: #000 !important;
  display: inline-block;
  @media (max-width: 560px) {
    font-size: 16px;
  }
  @media (max-width: 485px) {
    font-size: 12px;
    padding: 0.5rem 0rem;
  }
`;
const SpanText = styled.span`
  font-family: Outfit, sans-serif;
  text-decoration: none;
  color: #000 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
  @media (max-width: 560px) {
    font-size: 16px;
  }
  @media (max-width: 485px) {
    font-size: 12px;
    padding: 0.5rem 0rem;
    max-width: 120px;
  }
  @media (max-width: 355px) {
    font-size: 12px;
    padding: 0.5rem 0rem;
    max-width: 80px;
  }
`;
const StyledDropdownContent = styled(DropdownMenu.Content)`
  z-index: 1050;
  position: absolute;
  min-width: 150px;
  border-radius: 0.15rem;
  border: 1px solid ${({ theme }) => theme.primaryColor.primary};
  background-color: white;
  padding: 0.5rem;

  @media (max-width: 485px) {
    min-width: 103px;
    padding: 0.4rem;
  }
  @media (max-width: 355px) {
    min-width: 94px;
  }
`;
const StyledDropdownItem = styled(DropdownMenu.Item)`
  font-size: 18px;
  font-weight: 600;
  font-family: "Outfit", sans-serif;
  color: ${({ theme }) => theme.primaryColor.primary};
  outline: none;
  cursor: pointer;
  text-align: center;
  padding: 0.5rem;
  @media (max-width: 560px) {
    font-size: 16px;
  }
  @media (max-width: 485px) {
    font-size: 12px;
    padding: 0.4rem;
  }
`;
